import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt } from 'react-icons/fa';
import './HomeAgendaSection.css';

// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';

const HomeAgendaSection = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setCurrentTime] = useState(new Date()); // State to force re-render every minute

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 60000); // Update every minute
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                // Fetch upcoming events: dates >= today
                // Note: String comparison works for ISO dates (YYYY-MM-DD)
                const today = new Date().toISOString().split('T')[0];

                const q = query(
                    collection(db, 'agenda'),
                    where('date', '>=', today),
                    orderBy('date', 'asc'),
                    limit(6) // Increased limit for slider
                );

                const snapshot = await getDocs(q);
                const eventList = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setEvents(eventList);
            } catch (err) {
                console.error("Error fetching homepage agenda:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchEvents();
    }, []);

    if (loading) return null; // Don't show anything while loading
    if (events.length === 0) return null; // Hide section if no upcoming events

    return (
        <section className="agenda-home-section">
            <div className="container">
                <div className="agenda-home-header">
                    <h2 style={{ color: 'inherit' }}>
                        <FaCalendarAlt style={{ color: '#1890ff' }} />
                        Agenda Sekolah
                    </h2>
                </div>

                <Swiper
                    modules={[Navigation, Autoplay]}
                    spaceBetween={20}
                    slidesPerView={1}
                    navigation
                    autoplay={{
                        delay: 5000,
                        disableOnInteraction: false,
                    }}
                    breakpoints={{
                        640: {
                            slidesPerView: 2,
                        },
                        1024: {
                            slidesPerView: 3,
                        },
                    }}
                    className="agenda-slider"
                >
                    {events.map(ev => {
                        const dateObj = new Date(ev.date);
                        const day = dateObj.getDate();
                        const month = dateObj.toLocaleDateString('id-ID', { month: 'short' });

                        const targetDateStr = ev.startTime ? `${ev.date}T${ev.startTime}` : `${ev.date}T00:00:00`;
                        const targetDate = new Date(targetDateStr);
                        const now = new Date();
                        const diffMs = targetDate - now;

                        let countdownText = '';
                        let countdownColor = '#1890ff'; // Default blue

                        if (diffMs > 0) {
                            const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                            const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

                            if (days > 0) {
                                countdownText = `${days} Hari Lagi`;
                                countdownColor = days <= 2 ? '#faad14' : '#52c41a'; // Orange if close, Green if far
                            } else if (hours > 0) {
                                countdownText = `${hours} Jam Lagi`;
                                countdownColor = '#fa8c16'; // Darker orange
                            } else {
                                countdownText = 'Segera';
                                countdownColor = '#ff4d4f'; // Red
                            }
                        } else {
                            // Event passed or ongoing today
                            const daysPassed = Math.floor(Math.abs(diffMs) / (1000 * 60 * 60 * 24));
                            if (daysPassed === 0) {
                                countdownText = 'Hari Ini';
                                countdownColor = '#1890ff';
                            } else {
                                countdownText = 'Selesai';
                                countdownColor = '#8c8c8c';
                            }
                        }

                        return (
                            <SwiperSlide key={ev.id} style={{ height: 'auto' }}>
                                <div className="agenda-card-compact" style={{ borderLeftColor: ev.color || '#1890ff' }}>
                                    <div className="agenda-date-box">
                                        <span className="agenda-date-day">{day}</span>
                                        <span className="agenda-date-month">{month}</span>
                                    </div>
                                    <div className="agenda-content">
                                        <div className="agenda-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '5px' }}>
                                            <h3>{ev.title}</h3>
                                            {countdownText && (
                                                <span className="agenda-countdown-badge" style={{ background: countdownColor }}>
                                                    {countdownText}
                                                </span>
                                            )}
                                        </div>
                                        <div className="agenda-meta">
                                            <span><FaClock size={12} /> {ev.startTime}</span>
                                            {ev.location && <span><FaMapMarkerAlt size={12} /> {ev.location}</span>}
                                        </div>
                                    </div>
                                </div>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            </div>
        </section>
    );
};

export default HomeAgendaSection;
